/**
 * @element qds-card-basic
 * 
 * A simple card component with an icon, title, description, and action button.
 * 
 * @attr {string} title - The card title text
 * @attr {string} description - The card description text
 * @attr {string} icon - Icon to display (`prime:pi-*`, `lucide:*`, SVG string, or URL)
 * @attr {string} button-text - Text for the action button
 * @attr {string} href - URL that the card button links to
 * @attr {string} target - Where to open the link (_blank, _self, etc.)
 * 
 * @csspart card - The main card container
 * @csspart header - The card header section
 * @csspart icon - The icon element
 * @csspart content - The card content section
 * @csspart title - The title element
 * @csspart description - The description element
 * @csspart footer - The card footer section
 * @csspart button - The action button
 * 
 * @fires click - Fired when the card button is clicked
 */

import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { applyTypographyStyle } from '../shared/typography-utils.js';

// Custom event interface
interface CardBasicClickEventDetail {
  href: string;
}

type LucideRuntime = {
  createElement?: (icon: unknown) => SVGElement;
  [name: string]: unknown;
};

let lucideRuntimePromise: Promise<LucideRuntime | null> | null = null;

function toKebabCase(value: string): string {
  return value.replace(/([A-Z])/g, (match, letter, index) => index === 0 ? letter.toLowerCase() : `-${letter.toLowerCase()}`);
}

function getLucideRuntime(): LucideRuntime | null {
  const candidate = (globalThis as typeof globalThis & { lucide?: unknown }).lucide;
  return candidate && typeof candidate === 'object' ? candidate as LucideRuntime : null;
}

function loadLucideRuntime(): Promise<LucideRuntime | null> {
  const existing = getLucideRuntime();
  if (existing) return Promise.resolve(existing);
  if (!lucideRuntimePromise) {
    lucideRuntimePromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/lucide@latest/dist/umd/lucide.min.js';
      script.async = true;
      script.onload = () => resolve(getLucideRuntime());
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
  }
  return lucideRuntimePromise;
}

// Load the base CSS
loadBaseCSS();

export class QdsCardBasic extends HTMLElement {
  // Private properties
  private _title: string = '';
  private _description: string = '';
  private _icon: string = '';
  private _buttonText: string = 'Browse';
  private _href: string = '#';
  private _target: string = '_self';
  private _typography: Record<string, unknown> = {};
  private _boundClickHandler?: EventListener;

  static get observedAttributes(): string[] {
    return ['title', 'description', 'icon', 'button-text', 'href', 'target', 'typography'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.render();
  }
  
  // Lifecycle callbacks
  connectedCallback(): void {
    if (!this.isConnected) return;
    if (this.shadowRoot) void adoptPrimeIconsStyles(this.shadowRoot);
    this._attachClickHandler();
  }

  disconnectedCallback(): void {
    // Clean up event listeners
    const button = this.shadowRoot?.querySelector('.card-button');
    if (button && this._boundClickHandler) {
      button.removeEventListener('click', this._boundClickHandler);
    }
  }

  /**
   * Attaches the click handler to the current button node using a single
   * stable bound reference. Idempotent (removes before adding) so repeated
   * calls from render()/connectedCallback cannot accumulate duplicate
   * listeners.
   * @private
   */
  private _attachClickHandler(): void {
    if (!this.isConnected || !this.shadowRoot) return;
    this._boundClickHandler = this._boundClickHandler || ((event: Event) => this._handleClick(event as MouseEvent));
    const button = this.shadowRoot.querySelector('.card-button');
    if (button) {
      button.removeEventListener('click', this._boundClickHandler);
      button.addEventListener('click', this._boundClickHandler);
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;
    
    switch (name) {
      case 'title':
        this._title = newValue || '';
        break;
      case 'description':
        this._description = newValue || '';
        break;
      case 'icon':
        this._icon = newValue || '';
        break;
      case 'button-text':
        this._buttonText = newValue || 'Browse';
        break;
      case 'href':
        this._href = newValue || '#';
        break;
      case 'target':
        this._target = newValue || '_self';
        break;
      case 'typography':
        this._typography = this._parseTypographyAttribute(newValue);
        break;
    }

    this.render();
  }

  /**
   * Parses a JSON typography attribute value into a style map. Invalid JSON
   * is ignored (falls back to an empty map) so a malformed attribute cannot
   * throw during attribute processing.
   * @private
   */
  private _parseTypographyAttribute(value: string | null): Record<string, unknown> {
    if (!value) {
      return {};
    }
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }

  // Getters and setters
  get title(): string {
    return this._title;
  }
  
  set title(value: string) {
    this.setAttribute('title', value);
  }
  
  get description(): string {
    return this._description;
  }
  
  set description(value: string) {
    this.setAttribute('description', value);
  }
  
  get icon(): string {
    return this._icon;
  }
  
  set icon(value: string) {
    this.setAttribute('icon', value);
  }
  
  get buttonText(): string {
    return this._buttonText;
  }
  
  set buttonText(value: string) {
    this.setAttribute('button-text', value);
  }
  
  get href(): string {
    return this._href;
  }
  
  set href(value: string) {
    this.setAttribute('href', value);
  }
  
  get target(): string {
    return this._target;
  }
  
  set target(value: string) {
    this.setAttribute('target', value);
  }

  get typography(): Record<string, unknown> {
    return this._typography;
  }

  set typography(value: Record<string, unknown> | undefined) {
    this._typography = value ? { ...value } : {};
    this.render();
  }

  // Event handlers
  private _handleClick(event: MouseEvent): void {
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent<CardBasicClickEventDetail>('click', {
      bubbles: true,
      composed: true,
      detail: { href: this._href }
    }));
  }

  /** Replaces a declarative Lucide marker with its SVG once the library loads. */
  private _hydrateLucideIcon(): void {
    const marker = this.shadowRoot?.querySelector<HTMLElement>('[data-lucide]');
    const iconName = marker?.dataset.lucide;
    if (!marker || !iconName) return;

    void loadLucideRuntime().then((runtime) => {
      if (!runtime?.createElement || !marker.isConnected) return;
      const entry = Object.entries(runtime).find(([key]) => toKebabCase(key) === iconName);
      if (!entry) return;
      const svg = runtime.createElement(entry[1]);
      svg.setAttribute('class', 'card-icon');
      svg.setAttribute('part', 'icon');
      svg.setAttribute('aria-hidden', 'true');
      marker.replaceWith(svg);
    });
  }

  // Render the icon
  private _renderIcon(): string {
    // Default database icon if none provided
    if (!this._icon) {
      return `
        <svg class="card-icon" viewBox="0 0 24 24" fill="currentColor" part="icon">
          <path d="M4,5V7H20V5H4M4,9V11H20V9H4M4,13V15H20V13H4M4,17V19H20V17H4Z"></path>
        </svg>
      `;
    }
    
    if (this._icon.startsWith('prime:')) {
      const className = this._icon.slice('prime:'.length);
      if (/^pi-[a-z0-9-]+$/i.test(className)) {
        return `<i class="pi ${className} card-icon" part="icon" aria-hidden="true"></i>`;
      }
    }

    if (this._icon.startsWith('lucide:')) {
      const iconName = this._icon.slice('lucide:'.length);
      if (/^[a-z0-9-]+$/i.test(iconName)) {
        return `<i class="card-icon" data-lucide="${iconName}" part="icon" aria-hidden="true"></i>`;
      }
    }

    // Check if it's a URL or SVG content
    if (this._icon.startsWith('<svg') || this._icon.startsWith('<?xml')) {
      return `<div class="card-icon" part="icon">${this._icon}</div>`;
    } else {
      return `<img class="card-icon" src="${this._icon}" alt="${this._title}" part="icon">`;
    }
  }

  // Render the component
  private render(): void {
    if (!this.shadowRoot) return;
    
    const styles = `
      :host {
        display: block;
        --card-font-family: var(--qds-card-font-family, var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)));
        --card-bg-color: var(--qds-card-bg-color, var(--theme-background-primary));
        --card-text-color: var(--qds-card-text-color, var(--theme-text-primary));
        --card-muted-text-color: var(--qds-card-muted-text-color, var(--theme-text-secondary));
        --card-border-color: var(--qds-card-border-color, var(--theme-border-primary));
        --card-border-radius: var(--qds-card-radius, var(--theme-radius-md, 8px));
        --card-base-shadow: var(--qds-card-shadow, var(--theme-shadow-xs));
        --card-hover-shadow: var(--qds-card-hover-shadow, var(--theme-shadow-sm));
        --card-accent-color: var(--qds-accent-color, var(--theme-accent-primary));
        --card-title-font-size: var(--qds-card-title-font-size, var(--theme-font-size-md, 16px));
        --card-body-font-size: var(--qds-card-body-font-size, var(--theme-font-size-sm, 14px));
        --card-action-font-size: var(--qds-card-action-font-size, var(--theme-font-size-sm, 14px));
        --card-title-font-weight: var(--qds-card-title-font-weight, var(--theme-font-weight-semibold, 600));
        --card-action-font-weight: var(--qds-card-action-font-weight, var(--theme-font-weight-medium, 500));
        --card-header-padding-inline: var(--qds-card-header-padding-inline, var(--theme-space-4, 16px));
        --card-header-padding-block-start: var(--qds-card-header-padding-block-start, var(--theme-space-4, 16px));
        --card-header-padding-block-end: var(--qds-card-header-padding-block-end, var(--theme-space-2, 8px));
        --card-content-padding: var(--qds-card-content-padding, var(--theme-space-4, 16px));
        --card-footer-padding-block: var(--qds-card-footer-padding-block, var(--theme-space-3, 12px));
        --card-footer-padding-inline: var(--qds-card-footer-padding-inline, var(--theme-space-4, 16px));
        --card-divider-offset: var(--qds-card-divider-offset, var(--theme-space-3, 12px));
        --card-icon-size: var(--qds-card-icon-size, calc(var(--icon-size, 20px) + var(--theme-space-1, 4px)));
        --card-icon-gap: var(--qds-card-icon-gap, var(--theme-space-3, 12px));
        --card-button-icon-gap: var(--qds-card-button-icon-gap, var(--theme-space-1, 4px));
        --card-arrow-icon-size: var(--qds-card-arrow-icon-size, calc(var(--icon-size, 20px) - var(--theme-space-1, 4px)));
        --card-min-height: var(--qds-card-min-height, 220px);
      }
      
      .card {
        display: flex;
        flex-direction: column;
        font-family: var(--card-font-family);
        background-color: var(--card-bg-color);
        color: var(--card-text-color);
        border-radius: var(--card-border-radius);
        overflow: hidden;
        box-shadow: var(--card-base-shadow);
        transition: box-shadow var(--transition-speed-normal, 0.25s) ease;
        height: 100%;
        min-height: var(--card-min-height);
        border: 1px solid var(--card-border-color);
        position: relative;
      }
      
      .card:hover {
        box-shadow: var(--card-hover-shadow);
      }
      
      .card-header {
        display: flex;
        align-items: center;
        gap: var(--card-icon-gap);
        padding:
          var(--card-header-padding-block-start)
          var(--card-header-padding-inline)
          var(--card-header-padding-block-end);
        position: relative; /* For positioning the divider */
        z-index: 1; /* Keep above divider */
      }
      
      /* Horizontal divider line */
      .card-divider {
        height: 1px;
        width: 100%;
        background-color: var(--card-border-color);
        margin: var(--card-divider-offset) 0 0 0;
      }
      
      .card-icon {
        width: var(--card-icon-size);
        height: var(--card-icon-size);
        color: var(--card-accent-color);
        flex-shrink: 0;
      }
      
      .card-title {
        font-size: var(--card-title-font-size);
        font-weight: var(--card-title-font-weight);
        margin: 0;
        color: var(--card-text-color);
        position: relative;
        z-index: 1;
      }
      
      .card-content {
        padding: var(--card-content-padding);
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 1;
      }
      
      .card-description {
        font-size: var(--card-body-font-size);
        color: var(--card-muted-text-color);
        margin: 0;
        line-height: 1.4;
        flex-grow: 1;
      }
      
      .card-footer {
        padding: var(--card-footer-padding-block) var(--card-footer-padding-inline);
        display: flex;
        justify-content: flex-end;
        align-items: center;
      }
      
      .card-button {
        display: inline-flex;
        align-items: center;
        gap: var(--card-button-icon-gap);
        background: none;
        border: none;
        color: var(--card-accent-color);
        font-size: var(--card-action-font-size);
        font-weight: var(--card-action-font-weight);
        cursor: pointer;
        padding: 0;
        text-decoration: none;
      }

      .card-button:visited {
        color: var(--card-accent-color);
        text-decoration: none;
      }
      
      .card-button:hover {
        text-decoration: underline;
      }

      .card-button:focus-visible {
        outline: 2px solid var(--card-accent-color);
        outline-offset: 2px;
        border-radius: var(--theme-radius-sm, 4px);
        box-shadow: 0 0 0 4px var(--button-focus-ring);
      }
      
      .arrow-icon {
        width: var(--card-arrow-icon-size);
        height: var(--card-arrow-icon-size);
      }
    `;

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <div class="card" part="card">
        <div class="card-header" part="header">
          ${this._renderIcon()}
          <h3 class="card-title" part="title">${this._title}</h3>
        </div>
        <div class="card-divider" part="divider"></div>
        <div class="card-content" part="content">
          <p class="card-description" part="description">${this._description}</p>
        </div>
        <div class="card-footer" part="footer">
          <a 
            class="card-button" 
            href="${this._href}" 
            target="${this._target}" 
            part="button"
          >
            ${this._buttonText}
            <svg class="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.01 11H4v2h12.01v3L20 12l-3.99-4z"></path>
            </svg>
          </a>
        </div>
      </div>
    `;

    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.card-title'), this._typography.title);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.card-description'), this._typography.description);
    applyTypographyStyle(this.shadowRoot.querySelector<HTMLElement>('.card-button'), this._typography.buttonText);

    void adoptPrimeIconsStyles(this.shadowRoot);
    this._hydrateLucideIcon();
    this._attachClickHandler();
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-card-basic')) {
  customElements.define('qds-card-basic', QdsCardBasic);
}

// Export default for direct import
export default QdsCardBasic;
